// Payment Routing console (admin-portal, PayGate Phase 3): the platform view of
// multi-provider payout routing. Operators see each provider's live health, the
// capability registry, the rules the engine consults (platform defaults + a tenant's
// overrides), can add a rule, and can SIMULATE a route for an instruction shape
// without side effects. All data comes from paygate-service via the gateway.
//
// Routing is engine-driven only when PAYGATE_ROUTING_ENGINE is enabled on the service;
// until then these rules are advisory (the static per-tenant connector path runs). The
// banner makes that explicit so an operator never assumes a rule is live.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Row, Select,
  Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, PlusOutlined, ExperimentOutlined } from '@ant-design/icons';
import * as api from '../../services/routingService';

const { Title, Text, Paragraph } = Typography;

const COUNTRIES = ['TZ', 'KE', 'UG', 'RW'];
const CHANNELS = ['BANK', 'MOBILE_MONEY', 'ANY'];

const healthTag = (h: api.Health) => {
  const map: Record<api.Health, { color: string; label: string }> = {
    healthy: { color: 'green', label: 'Healthy' },
    degraded: { color: 'orange', label: 'Degraded' },
    unavailable: { color: 'red', label: 'Unavailable' },
  };
  const m = map[h] ?? { color: 'default', label: h };
  return <Tag color={m.color}>{m.label}</Tag>;
};

const reasonTag = (reason: string, fallbackUsed: boolean) => {
  if (reason === 'matched_rule') return <Tag color="green">Matched rule</Tag>;
  if (reason === 'fallback' || fallbackUsed) return <Tag color="orange">Health fallback</Tag>;
  return <Tag>Default rail (bank file)</Tag>;
};

export const PaymentRoutingPage: React.FC = () => {
  const [country, setCountry] = useState('TZ');
  const [providers, setProviders] = useState<api.ProviderStatus[]>([]);
  const [caps, setCaps] = useState<api.Capability[]>([]);
  const [rules, setRules] = useState<api.Rule[]>([]);
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();

  const [previewForm] = Form.useForm();
  const [decision, setDecision] = useState<api.RouteDecision | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, r] = await Promise.all([
        api.listProviders(country),
        api.listCapabilities(country),
        api.listRules(),
      ]);
      setProviders(p);
      setCaps(c);
      setRules(r);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load routing data');
    } finally {
      setLoading(false);
    }
  }, [country]);

  useEffect(() => {
    load();
  }, [load]);

  const submitRule = async () => {
    try {
      const v = await form.validateFields();
      await api.createRule({
        priority: v.priority,
        country: v.country,
        channel: v.channel,
        network: v.network || null,
        currency: v.currency || null,
        min_amount: v.min_amount ?? 0,
        max_amount: v.max_amount ?? null,
        provider_code: v.provider_code,
        fallback_provider_code: v.fallback_provider_code || null,
        health_aware: !!v.health_aware,
      });
      message.success('Routing rule created');
      setAddOpen(false);
      form.resetFields();
      await load();
    } catch (e: any) {
      if (e?.errorFields) return; // validation, already surfaced by the form
      message.error(e?.response?.data?.message || 'Could not create rule');
    }
  };

  const runPreview = async () => {
    try {
      const v = await previewForm.validateFields();
      setPreviewing(true);
      const d = await api.previewRoute({
        tenant_id: v.tenant_id || undefined,
        country: v.country,
        channel: v.channel,
        network: v.network || undefined,
        currency: v.currency || undefined,
        amount: v.amount ?? 0,
      });
      setDecision(d);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Could not simulate route');
    } finally {
      setPreviewing(false);
    }
  };

  const money = (n?: number | null) => (n == null ? '—' : n.toLocaleString());

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>Payment Routing</Title>
          <Text type="secondary">
            Multi-provider payout routing — provider health, capabilities and the rules the engine consults.
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={country} onChange={setCountry} style={{ width: 120 }}
              options={COUNTRIES.map((c) => ({ value: c, label: c }))} />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Engine-gated rollout"
        description="Routing rules are consulted only where the routing engine is enabled for the service. Until then they are advisory and the static per-tenant connector path runs. Simulation below always reflects what the engine would choose with current provider health."
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title="Provider health"
            loading={loading}
            extra={<Text type="secondary">last 24h outcomes</Text>}
          >
            {providers.length === 0 ? (
              <Empty description="No providers configured for this country" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => `${r.provider}-${r.country}`}
                dataSource={providers}
                columns={[
                  { title: 'Provider', dataIndex: 'provider', render: (v) => <Text strong>{v}</Text> },
                  { title: 'Health', dataIndex: 'health', render: (h) => healthTag(h) },
                  {
                    title: 'Channels', dataIndex: 'channels',
                    render: (ch?: string[]) => (ch || []).map((c) => <Tag key={c}>{c}</Tag>),
                  },
                ]}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Route simulator" extra={<ExperimentOutlined />}>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              Preview which provider an instruction would route to right now (uses live health). Side-effect free.
            </Paragraph>
            <Form form={previewForm} layout="vertical" initialValues={{ country, channel: 'MOBILE_MONEY', amount: 100000 }}>
              <Row gutter={12}>
                <Col span={12}><Form.Item name="country" label="Country" rules={[{ required: true }]}>
                  <Select options={COUNTRIES.map((c) => ({ value: c, label: c }))} /></Form.Item></Col>
                <Col span={12}><Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
                  <Select options={CHANNELS.filter((c) => c !== 'ANY').map((c) => ({ value: c, label: c }))} /></Form.Item></Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}><Form.Item name="network" label="Network"><Input placeholder="e.g. VODACOM" /></Form.Item></Col>
                <Col span={8}><Form.Item name="currency" label="Currency"><Input placeholder="e.g. TZS" /></Form.Item></Col>
                <Col span={8}><Form.Item name="amount" label="Amount (minor)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              </Row>
              <Form.Item name="tenant_id" label="Tenant ID (optional — platform defaults if blank)">
                <Input placeholder="UUID" />
              </Form.Item>
              <Button type="primary" onClick={runPreview} loading={previewing}>Simulate route</Button>
            </Form>
            {decision && (
              <Alert
                style={{ marginTop: 16 }}
                type={decision.reason === 'default_rail' ? 'warning' : 'success'}
                message={<Space>Routes to <Text strong>{decision.provider}</Text> {reasonTag(decision.reason, decision.fallbackUsed)}</Space>}
                description={decision.ruleVersion ? `Rule version ${decision.ruleVersion}` : 'No rule matched — provider-independent bank file.'}
              />
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title="Routing rules"
            loading={loading}
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add rule</Button>}
          >
            {rules.length === 0 ? (
              <Empty description="No active routing rules — every payout uses the default rail (bank file)" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={rules}
                columns={[
                  { title: 'Priority', dataIndex: 'priority', width: 90 },
                  {
                    title: 'Scope', dataIndex: 'tenant_id',
                    render: (t?: string | null) => (t ? <Tag color="blue">Tenant</Tag> : <Tag>Platform</Tag>),
                  },
                  { title: 'Country', dataIndex: 'country' },
                  { title: 'Channel', dataIndex: 'channel' },
                  { title: 'Network', dataIndex: 'network', render: (v?: string | null) => v || '—' },
                  { title: 'Currency', dataIndex: 'currency', render: (v?: string | null) => v || '—' },
                  { title: 'Min', dataIndex: 'min_amount', render: money },
                  { title: 'Max', dataIndex: 'max_amount', render: money },
                  { title: 'Provider', dataIndex: 'provider_code', render: (v) => <Text strong>{v}</Text> },
                  {
                    title: 'Fallback', dataIndex: 'fallback_provider_code',
                    render: (v?: string | null, r?: api.Rule) =>
                      v ? <Space size={4}>{v}{r?.health_aware && <Tag color="orange">health-aware</Tag>}</Space> : '—',
                  },
                  { title: 'Ver', dataIndex: 'version', width: 60 },
                ]}
                scroll={{ x: 'max-content' }}
              />
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card title={`Capability registry — ${country}`} loading={loading}>
            {caps.length === 0 ? (
              <Empty description="No capabilities registered for this country" />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey={(c) => `${c.provider_code}-${c.channel}-${c.currency}`}
                dataSource={caps}
                columns={[
                  { title: 'Provider', dataIndex: 'provider_code', render: (v) => <Text strong>{v}</Text> },
                  { title: 'Channel', dataIndex: 'channel' },
                  { title: 'Currency', dataIndex: 'currency' },
                  { title: 'Min', dataIndex: 'min_amount', render: money },
                  { title: 'Max', dataIndex: 'max_amount', render: money },
                  { title: 'Enabled', dataIndex: 'enabled', render: (e) => (e ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
                ]}
                scroll={{ x: 'max-content' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="Add routing rule"
        open={addOpen}
        onOk={submitRule}
        onCancel={() => setAddOpen(false)}
        okText="Create rule"
        width={640}
      >
        <Form form={form} layout="vertical" initialValues={{ country, channel: 'MOBILE_MONEY', priority: 100, min_amount: 0, health_aware: true }}>
          <Row gutter={12}>
            <Col span={8}><Form.Item name="priority" label="Priority" rules={[{ required: true }]} tooltip="Lower = evaluated first">
              <InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="country" label="Country" rules={[{ required: true }]}>
              <Select options={COUNTRIES.map((c) => ({ value: c, label: c }))} /></Form.Item></Col>
            <Col span={8}><Form.Item name="channel" label="Channel" rules={[{ required: true }]}>
              <Select options={CHANNELS.map((c) => ({ value: c, label: c }))} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="network" label="Network (optional)"><Input placeholder="e.g. VODACOM" /></Form.Item></Col>
            <Col span={12}><Form.Item name="currency" label="Currency (optional)"><Input placeholder="e.g. TZS" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="min_amount" label="Min amount (minor)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="max_amount" label="Max amount (minor, blank = none)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="provider_code" label="Provider" rules={[{ required: true }]}>
              <Input placeholder="e.g. selcom, mpesa, bankfile" /></Form.Item></Col>
            <Col span={12}><Form.Item name="fallback_provider_code" label="Fallback provider (optional)">
              <Input placeholder="used when primary is degraded" /></Form.Item></Col>
          </Row>
          <Form.Item name="health_aware" label="Health-aware (use fallback when primary degraded)" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="tenant_id" label="Tenant ID (optional — platform default if blank)">
            <Input placeholder="UUID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentRoutingPage;
